import React from "react";

/**
 * Returns a JSX summary bubble for the user's answer by question type.
 */
export function formatAnswer(answer, questionType) {
    const toSrc = (item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        if (typeof item === "object") {
            if (typeof item.dataURL === "string") return item.dataURL;
            if (typeof item.data === "string") return item.data;
        }
        return null;
    };

    switch (questionType) {
        case "personal-info":
            return (
                <p>
                    <strong>Name:</strong> {answer.name}
                    <br />
                    <strong>Date of Birth:</strong> {answer.dateOfBirth}
                </p>
            );

        case "sex":
            return <p><strong>Sex assigned at birth:</strong> {answer}</p>;

        case "menstrual-cycle":
            return <p><strong>Menstrual cycle:</strong> {answer}</p>;

        case "contraceptives": {
            let displayText = "";
            if (Array.isArray(answer)) {
                displayText = answer.length === 0 ? "None of the above" : answer.join(", ");
            } else if (typeof answer === "object" && Array.isArray(answer.selections)) {
                if (answer.selections.length === 0) {
                    displayText = "None of the above";
                } else {
                    const parts = [...answer.selections];
                    if (answer.selections.includes("Other") && answer.otherText) {
                        const otherIndex = parts.indexOf("Other");
                        parts[otherIndex] = `Other: ${answer.otherText}`;
                    }
                    displayText = parts.join(", ");
                }
            } else {
                displayText = String(answer);
            }
            return <p><strong>Contraceptives:</strong> {displayText}</p>;
        }

        case "contact-info":
            return (
                <p>
                    <strong>Phone:</strong> {answer.phoneNumber}
                    <br />
                    <strong>Email:</strong> {answer.email}
                </p>
            );

        case "address":
            return <p><strong>Address:</strong> {answer}</p>;

        case "treatment":
            return <p><strong>Treatment:</strong> {answer}</p>;

        case "duration":
            return <p><strong>Duration:</strong> {answer}</p>;

        case "has-allergies":
            return <p><strong>Has allergies:</strong> {answer === "y" ? "Yes" : answer === "n" ? "No" : String(answer)}</p>;

        case "allergies":
            return <p><strong>Allergies:</strong> {answer}</p>;

        case "photo-upload": {
            if (Array.isArray(answer)) {
                const urls = answer.map(toSrc).filter(Boolean);
                const count = urls.length;

                return (
                    <div>
                        <p><strong>Photos uploaded:</strong> {count} {count === 1 ? "photo" : "photos"}</p>
                        {count > 0 && (
                            <div className="photo-summary-grid">
                                {urls.slice(0, 3).map((src, index) => (
                                    <img
                                        key={index}
                                        src={src}
                                        alt=""
                                        aria-hidden="true"
                                        className="photo-summary-thumbnail"
                                    />
                                ))}
                                {count > 3 && (
                                    <p className="photo-more">+{count - 3} more</p>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
            return <p><strong>Photos uploaded:</strong> {String(answer)}</p>;
        }

        case "payment": {
            // Support normalized object or legacy string
            if (answer && typeof answer === "object") {
                if (answer.type === "insurance") {
                    return (
                        <p>
                            <strong>Payment:</strong> Insurance
                            {answer.provider ? ` — ${answer.provider}` : ""}
                        </p>
                    );
                }
                if (answer.type === "self-pay" || answer.type === "out-of-pocket") {
                    return <p><strong>Payment:</strong> Out of pocket</p>;
                }
            }
            return <p><strong>Payment:</strong> {String(answer)}</p>;
        }

        case "credit-card":
            // Hide credit card details entirely
            return null;

        case "state":
            return <p><strong>State:</strong> {answer}</p>;

        case "additional":
            return <p><strong>Additional information:</strong> {answer}</p>;

        case "referral":
            return <p><strong>How you heard about us:</strong> {answer}</p>;

        case "other-meds":
            if (answer?.has === "n") return <p><strong>Other medications:</strong> No</p>;
            return (
                <p>
                    <strong>Other medications:</strong> Yes
                    {Array.isArray(answer?.meds) && answer.meds.length > 0 && (
                        <>
                            <br />
                            <strong>List:</strong> {answer.meds.join(", ")}
                        </>
                    )}
                </p>
            );

        case "maternity": {
            const sel = Array.isArray(answer?.selections) ? answer.selections : [];
            const methods = Array.isArray(answer?.birthControlMethods)
                ? answer.birthControlMethods
                : [];
            const other = answer?.otherBirthControl;
            return (
                <p>
                    <strong>Pregnancy status:</strong> {sel.join(", ") || "(none)"}
                    <br />
                    {sel.includes("Currently on birth control") && (
                        <>
                            <strong>Birth control methods:</strong>{" "}
                            {methods.join(", ") || "(none)"}
                            <br />
                            {methods.includes("Other") && other ? (
                                <>
                                    <strong>Other:</strong> {other}
                                </>
                            ) : null}
                        </>
                    )}
                </p>
            );
        }

        case "tried":
            return (
                <p>
                    <strong>Tried:</strong>{" "}
                    {Array.isArray(answer) ? answer.join(", ") : String(answer)}
                </p>
            );

        case "prescriptions":
            if (!Array.isArray(answer) || answer.length === 0) {
                return <p><strong>Prescriptions tried:</strong> (none)</p>;
            }
            return (
                <div>
                    <strong>Prescriptions tried:</strong>
                    <ul style={{ marginTop: 6 }}>
                        {answer.map((m, i) => {
                            // Handle formats:
                            // 1. Legacy: string (medication name only)
                            if (typeof m === "string") {
                                return <li key={i}>{m}</li>;
                            }
                            // 2. Object format: name, duration, help (and optionally dosage for Acne)
                            const medName = (m.name === "Other" ? m.customName : m.name) || "(unnamed)";
                            const parts = [m.duration, m.help];
                            if (m.dosage) parts.push(m.dosage);
                            return (
                                <li key={i}>
                                    {medName} — {parts.join("; ")}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );

        case "minor-consent":
            return (
                <p>
                    <strong>Parent/Guardian:</strong> {answer?.name || "(name missing)"}
                    <br />
                    <strong>Relationship:</strong> {answer?.relationship || "(not set)"}
                </p>
            );

        case "minor-solo-treatment":
            return (
                <p>
                    <strong>Solo treatment consent:</strong>{" "}
                    {answer === true ? "Yes" : answer === false ? "No" : "(not set)"}
                </p>
            );

        case "minor-weight":
            return <p><strong>Minor weight:</strong> {answer} lbs</p>;

        case "parent-instructions":
            // Make sure we don't dump the object into JSX
            return <p><strong>Parent instructions acknowledged.</strong></p>;

        case "insurance-method":
            return (
                <p>
                    <strong>Insurance submission:</strong>{" "}
                    {{
                        photos: "Upload Photos",
                        id: "Enter Member ID",
                        skip: "Add it later",
                    }[answer] || String(answer)}
                </p>
            );

        case "insurance-info": {
            const rel = answer?.policyOwner?.relationship || "Self";
            return (
                <p>
                    <strong>Member ID:</strong> {answer?.memberID || "(missing)"}
                    <br />
                    <strong>Relationship:</strong> {rel}
                    <br />
                    {rel !== "Self" && (
                        <>
                            <strong>Policy Owner:</strong> {answer?.policyOwner?.fname}{" "}
                            {answer?.policyOwner?.lname}
                            <br />
                            <strong>Gender:</strong> {answer?.policyOwner?.gender}
                            <br />
                            <strong>DOB:</strong> {answer?.policyOwner?.dob}
                        </>
                    )}
                </p>
            );
        }

        case "insurance-photos": {
            const ins = answer?.insurance;
            const front1 = toSrc(ins?.cardFront);
            const back1 = toSrc(ins?.cardBack);

            const frontArr = Array.isArray(answer?.cardFront) ? answer.cardFront : [];
            const backArr = Array.isArray(answer?.cardBack) ? answer.cardBack : [];
            const frontLegacy = frontArr[0]?.data || answer?.cardFront?.data || null;
            const backLegacy = backArr[0]?.data || answer?.cardBack?.data || null;

            const frontSrc = front1 || frontLegacy || null;
            const backSrc = back1 || backLegacy || null;

            const hasFront = !!frontSrc;
            const hasBack = !!backSrc;

            return (
                <div>
                    <p>
                        <strong>Insurance card:</strong> front {hasFront ? "✓" : "✗"}, back {hasBack ? "✓" : "—"}
                    </p>
                    {(hasFront || hasBack) && (
                        <div className="photo-summary-grid">
                            {hasFront && <img src={frontSrc} alt="Card front" className="photo-summary-thumbnail" />}
                            {hasBack && <img src={backSrc} alt="Card back" className="photo-summary-thumbnail" />}
                        </div>
                    )}
                </div>
            );
        }

        default:
            return <p>{String(answer)}</p>;
    }
}
